/**
 * requestGuard.ts
 *
 * Tiny, framework-free helper for protecting an in-flight async request
 * against being superseded by a newer one that resolves first — i.e. "stale
 * response" protection. Each call to `next()` invalidates every previously
 * issued id; only the request holding the most-recently-issued id is
 * considered current.
 *
 * Kept as a pure, dependency-free utility (no React/Redux) so it can be
 * unit tested in isolation and reused by any ASN Gap Overview request owner
 * (currently `AsnGapDashboard`'s "ASN Loads vs Non-ASN Loads" chart fetch).
 *
 * Usage:
 *   const guard = createRequestGuard();
 *   const runFetch = async () => {
 *     const requestId = guard.next();
 *     const data = await someApiCall();
 *     if (!guard.isCurrent(requestId)) return; // a newer request has since started — discard
 *     setData(data);
 *   };
 */
export interface RequestGuard {
  /** Issues (and becomes) the new current request id. */
  next: () => number;
  /** True only when `id` is still the most-recently-issued id. */
  isCurrent: (id: number) => boolean;
}

export const createRequestGuard = (): RequestGuard => {
  let currentId = 0;
  return {
    next: () => {
      currentId += 1;
      return currentId;
    },
    isCurrent: (id: number) => id === currentId,
  };
};
